import React from "react";

import {
  IconButton,
  IconButtonProps,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon } from "icons";

const hasWriteText =
  "clipboard" in navigator && "writeText" in navigator.clipboard;

const ClickToCopy = ({
  value,
  label,
  ...props
}: { value?: string | number; label: string } & Omit<
  IconButtonProps,
  "aria-label"
>): React.ReactElement => {
  const toast = useToast();
  const onClick = React.useCallback(() => {
    value &&
      navigator.clipboard
        .writeText(`${value}`)
        .then(() => {
          toast({
            position: "top-right",
            description: "Copied to clipboard",
            duration: 2000,
          });
        })
        .catch(() => {
          toast({
            position: "top-right",
            description: "Copied to clipboard not supported",
            duration: 2000,
            status: "error",
          });
        });
    return value;
  }, [value, toast]);

  const helperText = React.useMemo(
    () =>
      hasWriteText
        ? `Click to copy ${label} to clipboard`
        : "Copy to clipboard not supported in your browser",
    [label]
  );
  return (
    <Tooltip
      label={helperText}
      aria-label="Click to copy helper text"
      defaultIsOpen={false}
      openDelay={500}
    >
      <IconButton
        icon={<CopyIcon />}
        onClick={onClick}
        aria-label={helperText}
        variant="link"
        colorScheme="black"
        minWidth={0}
        isDisabled={!hasWriteText}
        {...props}
      />
    </Tooltip>
  );
};

export default ClickToCopy;
